// Список матч-центра из ответа дня — чистые функции (без DOM). Порядок и состав — сервера (Р-3), клиент не сортирует:
//   1) блок топ-лиг — top_section.leagues в порядке top_tournaments ответа (iOS sortedTopLeagues, «no client resort»;
//      лиги, которых нет в top_tournaments, — следом в порядке top_section); раскрыт всегда;
//   2) страны — countries в порядке ответа, внутри — лиги и матчи в порядке ответа.
// Сжатый формат: команды и лиги строк — id в словарях teams{} / leagues{} ответа; полный — объекты в строке.
// Имена — как в ответе (name_localized, запас name), не переводятся.
import type { CountryRef, DayMatch, LeagueRef, MatchDayResponse, NamedRef } from '../../api/matchDay';
import { isLiveRow } from './live';

export interface TeamView { id: number | null; name: string; logo: string | null }
export interface MatchView { row: DayMatch; home: TeamView; away: TeamView }
export interface LeagueView { id: number; name: string; logo: string | null; countryName: string | null; matches: MatchView[] }
export interface SectionView {
  /** ключ раскрытия: top — блок топ-лиг, иначе c<id страны> */
  key: string;
  top: boolean;
  name: string;
  flag: string | null;
  leagues: LeagueView[];
  matchCount: number;
  liveCount: number;
}

export const nameOf = (ref: { name?: string | null; name_localized?: string | null } | null | undefined) =>
  ref?.name_localized || ref?.name || '';
const logoOf = (ref: NamedRef | null | undefined) => ref?.logo_thumb || ref?.logo || ref?.image || null;
const flagOf = (c: CountryRef | null | undefined) => c?.flag || c?.flag_url || c?.image || null;

function team(day: MatchDayResponse, ref: NamedRef | null | undefined, id: number | undefined): TeamView {
  const t = ref ?? (id != null ? day.teams?.[String(id)] : undefined);
  return { id: t?.id ?? id ?? null, name: nameOf(t), logo: logoOf(t) };
}

function matchViews(day: MatchDayResponse, rows: DayMatch[], keep: (m: DayMatch) => boolean): MatchView[] {
  return rows.filter(keep).map((row) => ({ row, home: team(day, row.home, row.home_id), away: team(day, row.away, row.away_id) }));
}

function league(day: MatchDayResponse, ref: LeagueRef | undefined, id: number, countryName: string | null, matches: MatchView[]): LeagueView {
  const l = ref ?? day.leagues?.[String(id)];
  return { id, name: nameOf(l), logo: logoOf(l), countryName: countryName ?? (nameOf(l?.country) || null), matches };
}

/** Блок топ-лиг по порядку top_tournaments. */
export function orderedTopLeagues<T extends { league_id?: number; id?: number }>(leagues: T[], tournaments: Array<{ id: number }> | undefined): T[] {
  if (!tournaments?.length) return leagues;
  const rank = new Map(tournaments.map((t, i) => [t.id, i]));
  const id = (l: T) => l.league_id ?? l.id ?? -1;
  return leagues
    .map((l, i) => ({ l, i, r: rank.get(id(l)) ?? tournaments.length + i }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.l);
}

export interface BuildOptions { liveOnly: boolean; field: string; topTitle: string }

/** Секции экрана; liveOnly — только живые строки (пустые лиги и страны уходят). */
export function buildSections(day: MatchDayResponse | undefined, opts: BuildOptions): SectionView[] {
  if (!day) return [];
  const keep = opts.liveOnly ? (m: DayMatch) => isLiveRow(m, opts.field) : () => true;
  const count = (leagues: LeagueView[]) => {
    const rows = leagues.flatMap((l) => l.matches.map((m) => m.row));
    return { matchCount: rows.length, liveCount: rows.filter((m) => isLiveRow(m, opts.field)).length };
  };
  const out: SectionView[] = [];
  const topLeagues = orderedTopLeagues(day.top_section?.leagues ?? [], day.top_tournaments)
    .map((l) => league(day, l, l.league_id ?? l.id, null, matchViews(day, l.matches ?? [], keep)))
    .filter((l) => l.matches.length);
  if (topLeagues.length) out.push({ key: 'top', top: true, name: opts.topTitle, flag: null, leagues: topLeagues, ...count(topLeagues) });
  for (const c of day.countries ?? []) {
    const cname = nameOf(c);
    const leagues = c.leagues
      .map((l) => league(day, l.league, l.league_id, cname, matchViews(day, l.matches ?? [], keep)))
      .filter((l) => l.matches.length);
    if (!leagues.length) continue;
    out.push({ key: `c${c.id ?? c.code ?? cname}`, top: false, name: cname, flag: flagOf(c), leagues, ...count(leagues) });
  }
  return out;
}

/** Раскрыта ли страна. Блок топ-лиг — всегда. Тап человека главнее правил. Иначе: страна единственная в списке (один
 *  матч в дне — как iOS) или включён LIVE (в списке и так только живые) — раскрыта; по умолчанию свёрнута. */
export function isExpanded(section: SectionView, opts: { toggled: Record<string, boolean>; liveOnly: boolean; countries: number }): boolean {
  if (section.top) return true;
  const own = opts.toggled[section.key];
  if (own !== undefined) return own;
  return opts.liveOnly || opts.countries === 1;
}

/** Счётчик шапки страны: «живые / все», без живых — «все» (как iOS/Android). */
export const countLabel = (s: Pick<SectionView, 'matchCount' | 'liveCount'>) => (s.liveCount > 0 ? `${s.liveCount} / ${s.matchCount}` : String(s.matchCount));
